/**
 * MONSTER TOWN — SHARED VENUE FLOOR FIXTURES. VENUE CREW. DATA ONLY.
 *
 * The furniture EVERY venue uses: the counter with its bell, tables and stools
 * to sit at, the start-over bin, and the three things a kid saves up for (the
 * display case, the picture menu, the sundae pedestal). None of it is ice-cream
 * specific — the restaurant reuses every piece here unchanged, which is half of
 * why venue #2 is a data drop.
 *
 * Split out of `venue.ts` for the 500-line house rule; `venue.ts` re-exports
 * everything here so no other file had to change.
 */

import { FOOT_1x1, FOOT_2x1 } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import {
  binArt,
  counterArt,
  displayCaseArt,
  menuBoardArt,
  specialArt,
  stoolArt,
  tableArt,
} from '../venue/fixtures'
import { ICE_CREAM_ITEMS } from './menu'

const id = (s: string): PlaceableId => s as PlaceableId

// ---------------------------------------------------------------------------
// the shop floor
// ---------------------------------------------------------------------------

export const venueCounter = definePlaceable<{ rings: number }>({
  id: id('venue_counter'),
  devName: 'Serving counter',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wood', 'wallMint', 'wallPink'],
  tags: ['venue', 'starter', 'venue:bell'],
  initialState: () => ({ rings: 0 }),
  buildArt: (ctx) => counterArt(ctx),
  // LAW #1: ring the bell and the next customer comes in early.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'ring', seconds: 0.5, target: 'bell' },
      { kind: 'particles', effect: 'note', count: 5 },
      { kind: 'spawn', what: 'venue:bell' },
      { kind: 'sound', cue: 'bell_ding' },
      { kind: 'state', patch: { rings: ctx.state.rings + 1 } },
    ],
    cooldownMs: 400,
  }),
})

export const venueTable = definePlaceable<{ served: number }>({
  id: id('venue_table'),
  devName: 'Table for two',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wood', 'wallMint', 'wallLilac'],
  tags: ['venue', 'seat', 'starter'],
  initialState: () => ({ served: 0 }),
  buildArt: (ctx) => tableArt(ctx),
  // LAW #1: tapping a table always wobbles it; if someone is sitting there
  // hoping for ice cream, it also hands them what you are carrying.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'wobble', seconds: 0.4, target: 'top' },
      { kind: 'particles', effect: 'sparkle', count: 4 },
      { kind: 'spawn', what: 'venue:serve' },
      { kind: 'sound', cue: 'table_tap' },
      { kind: 'state', patch: { served: ctx.state.served + 1 } },
    ],
    cooldownMs: 300,
  }),
})

export const venueStool = definePlaceable<{ served: number }>({
  id: id('venue_stool'),
  devName: 'Stool',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['cushion', 'fabric', 'fabricAlt', 'wallMint'],
  tags: ['venue', 'seat', 'starter'],
  initialState: () => ({ served: 0 }),
  buildArt: (ctx) => stoolArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'wobble', seconds: 0.4, target: 'seat' },
      { kind: 'particles', effect: 'sparkle', count: 4 },
      { kind: 'spawn', what: 'venue:serve' },
      { kind: 'sound', cue: 'table_tap' },
      { kind: 'state', patch: { served: ctx.state.served + 1 } },
    ],
    cooldownMs: 300,
  }),
})

export const venueBin = definePlaceable<{ dumped: number }>({
  id: id('venue_bin'),
  devName: 'Start-over bin',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tags: ['venue', 'starter', 'venue:clear'],
  initialState: () => ({ dumped: 0 }),
  buildArt: (ctx) => binArt(ctx),
  // A mis-tap is never punished: this is the friendly undo for a wonky sundae.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'flip', seconds: 0.4, target: 'lid' },
      { kind: 'particles', effect: 'poof', count: 7 },
      { kind: 'spawn', what: 'venue:clear' },
      { kind: 'sound', cue: 'poof' },
      { kind: 'state', patch: { dumped: ctx.state.dumped + 1 } },
    ],
    cooldownMs: 250,
  }),
})

export const venueDisplay = definePlaceable<{ looks: number }>({
  id: id('venue_display'),
  devName: 'Chilled display case',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 140,
  tier: 'tier1',
  stackable: false,
  walkable: false,
  tags: ['venue', 'decor'],
  initialState: () => ({ looks: 0 }),
  buildArt: (ctx) => displayCaseArt(ctx, ICE_CREAM_ITEMS),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'chill', seconds: 0.7, target: 'glass' },
      { kind: 'particles', effect: 'frost', count: 8 },
      { kind: 'sound', cue: 'chill' },
      { kind: 'state', patch: { looks: ctx.state.looks + 1 } },
    ],
  }),
})

export const venueMenu = definePlaceable<{ hints: number }>({
  id: id('venue_menu'),
  devName: 'Picture menu board',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 60,
  stackable: false,
  walkable: false,
  tags: ['venue', 'decor', 'venue:hint'],
  initialState: () => ({ hints: 0 }),
  buildArt: (ctx) => menuBoardArt(ctx, ICE_CREAM_ITEMS),
  // Tap it and every waiting order bounces — the "what did they want again?"
  // button. Memory help, never a penalty for forgetting.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'shine', seconds: 0.6, target: 'board' },
      { kind: 'particles', effect: 'sparkle', count: 6 },
      { kind: 'spawn', what: 'venue:hint' },
      { kind: 'sound', cue: 'sparkle_soft' },
      { kind: 'state', patch: { hints: ctx.state.hints + 1 } },
    ],
    cooldownMs: 300,
  }),
})

export const venueSpecial = definePlaceable<{ made: number }>({
  id: id('venue_special'),
  devName: "Today's special pedestal",
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 200,
  tier: 'tier2',
  stackable: false,
  walkable: false,
  tags: ['venue', 'treat', 'venue:special'],
  initialState: () => ({ made: 0 }),
  buildArt: (ctx) => specialArt(ctx, ICE_CREAM_ITEMS),
  // The expensive treat that earns its price: one tap builds a whole sundae.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'spin', seconds: 0.7, target: 'serving' },
      { kind: 'particles', effect: 'stars', count: 10 },
      { kind: 'spawn', what: 'venue:special' },
      { kind: 'sound', cue: 'chime_happy' },
      { kind: 'state', patch: { made: ctx.state.made + 1 } },
    ],
    cooldownMs: 400,
  }),
})


/** Registered through `venue.ts`, which spreads this into `VENUE_CONTENT`. */
export const VENUE_FLOOR_CONTENT = [
  venueCounter,
  venueTable,
  venueStool,
  venueBin,
  venueDisplay,
  venueMenu,
  venueSpecial,
]

/** Plain shop furniture: a kid can always lay out a shop with no coins at all. */
export const VENUE_FLOOR_FREE_IDS: PlaceableId[] = [
  venueCounter.id,
  venueTable.id,
  venueStool.id,
  venueBin.id,
]
